import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";

// Convert a Firestore Timestamp (has .seconds) or a plain Date into a JS Date
export function toJsDate(value) {
  if (!value) return null;
  if (value.seconds) return new Date(value.seconds * 1000);
  if (value instanceof Date) return value;
  return null;
}

// Builds the same "parties enriched with their transaction" list used by both
// the All Transactions list (PartyAllOverview) and the Dashboard (PartyDashboard),
// bucketed by transaction status.
export default function usePartyTransactionsByStatus() {
  const { currentUserProfile } = useSelector((state) => state.profile);
  const { partiesForUser, transactionsForUser, docsNeedingSignature } =
    useSelector((state) => state.party);
  const [loaded, setLoaded] = useState(false);
  const [partiesForDisplay, setPartiesForDisplay] = useState([]);
  const [underContractTransactions, setUnderContractTransactions] = useState(
    []
  );
  const [activeTransactions, setActiveTransactions] = useState([]);
  const [completedTransactions, setCompletedTransactions] = useState([]);
  const [archivedTransactions, setArchivedTransactions] = useState([]);
  const parties = partiesForUser?.parties;

  useEffect(() => {
    if (
      transactionsForUser?.length > 0 &&
      parties?.length > 0 &&
      currentUserProfile
    ) {
      let partiesWithTransaction = [];
      let underContract = [];
      let active = [];
      let completed = [];
      let archived = [];

      // Deduplicate parties by transactionId - only keep the first party per transaction
      // This handles the case where secondary/tertiary clients share the same email
      const seenTransactionIds = new Set();
      const uniqueParties = parties.filter((party) => {
        if (seenTransactionIds.has(party.transactionId)) {
          return false; // Skip this party, we already have one for this transaction
        }
        seenTransactionIds.add(party.transactionId);
        return true;
      });

      uniqueParties.forEach((party, index) => {
        const partyCopy = _.cloneDeep(party);
        const transaction = transactionsForUser.filter(
          (trans) => trans.id === party.transactionId
        )[0];
        if (transaction) {
          // Wait until after transaction to add party or extra parties appear
          partiesWithTransaction[index] = partyCopy;
          const transactionCopy = _.cloneDeep(transaction);

          // Add all existing fields
          partiesWithTransaction[index]["address"] =
            transactionCopy.address || {};
          partiesWithTransaction[index]["agentFirstName"] =
            transactionCopy.agentFirstName ||
            transactionCopy.agentProfile?.firstName ||
            "";
          partiesWithTransaction[index]["agentLastName"] =
            transactionCopy.agentLastName ||
            transactionCopy.agentProfile?.lastName ||
            "";
          partiesWithTransaction[index]["agentRepresents"] =
            transactionCopy.agentRepresents || "";
          partiesWithTransaction[index]["client"] =
            transactionCopy.client || {};
          partiesWithTransaction[index]["clientSecondary"] =
            transactionCopy.clientSecondary || {};
          partiesWithTransaction[index]["clientThird"] =
            transactionCopy.clientThird || {};
          partiesWithTransaction[index]["mlsNumbers"] =
            transactionCopy.mlsNumbers || [];
          partiesWithTransaction[index]["title"] = transactionCopy.title || "";
          partiesWithTransaction[index]["transactionPic"] =
            transactionCopy.pic || "";

          // Add new fields for the enhanced table
          partiesWithTransaction[index]["status"] =
            transactionCopy.status || "";
          partiesWithTransaction[index]["updatedAt"] =
            transactionCopy.updatedAt || null;
          partiesWithTransaction[index]["closingDateTime"] =
            transactionCopy.closingDateTime || null;
          partiesWithTransaction[index]["createdAt"] =
            transactionCopy.createdAt || null;

          // Check if user needs to sign documents
          const currentUserEmail = currentUserProfile?.email;
          const docsNeedingSigning =
            docsNeedingSignature?.filter(
              (doc) =>
                doc.transactionId === transaction.id &&
                doc.signingRequestedFor &&
                doc.signingRequestedFor.includes(currentUserEmail) &&
                (doc.status === "Awaiting Signature" ||
                  doc.status === "In Progress") &&
                !doc.signingComplete
            ) || [];

          partiesWithTransaction[index]["needsSignatures"] =
            docsNeedingSigning.length > 0;

          // Categorize by status
          const partyWithTrans = partiesWithTransaction[index];
          if (transactionCopy.status === "Under Contract") {
            underContract.push(partyWithTrans);
          } else if (
            transactionCopy.status === "Active Buyer" ||
            transactionCopy.status === "Active Listing"
          ) {
            active.push(partyWithTrans);
          } else if (transactionCopy.status === "Complete") {
            completed.push(partyWithTrans);
          } else if (transactionCopy.status === "Archived") {
            archived.push(partyWithTrans);
          }
        }
      });

      // Sort each category by default (Last Updated descending, except Completed which sorts by Closing Date descending)
      const sortByLastUpdated = (a, b) => {
        const dateA = a.updatedAt
          ? new Date(a.updatedAt.seconds * 1000)
          : new Date(0);
        const dateB = b.updatedAt
          ? new Date(b.updatedAt.seconds * 1000)
          : new Date(0);
        return dateB - dateA;
      };

      const sortByClosingDate = (a, b) => {
        const dateA = a.closingDateTime
          ? new Date(a.closingDateTime.seconds * 1000)
          : new Date(0);
        const dateB = b.closingDateTime
          ? new Date(b.closingDateTime.seconds * 1000)
          : new Date(0);
        return dateB - dateA;
      };

      setUnderContractTransactions(underContract.sort(sortByLastUpdated));
      setActiveTransactions(active.sort(sortByLastUpdated));
      setCompletedTransactions(completed.sort(sortByClosingDate));
      setArchivedTransactions(archived.sort(sortByLastUpdated));
      // partiesWithTransaction is built by assigning to partiesWithTransaction[index]
      // only when a matching transaction is found, which leaves sparse holes
      // (undefined) for any other indices. filter/map skip those holes, but
      // find/some/every visit them and pass undefined - filter them out here so
      // every consumer of partiesForDisplay can safely assume real party objects.
      setPartiesForDisplay(partiesWithTransaction.filter(Boolean));
    }
    setLoaded(true);
  }, [parties, transactionsForUser, docsNeedingSignature, currentUserProfile]);

  return {
    loaded,
    currentUserProfile,
    parties,
    partiesForDisplay,
    underContractTransactions,
    activeTransactions,
    completedTransactions,
    archivedTransactions,
  };
}
